import { DollarSign, Users, TrendingUp, BarChart3, Banknote, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ManagementReportMenu } from "@/components/Holizontal/ManagementReportMenu";
import StatCard from "@/components/StatCard";
import { apiFetch } from "@/lib/api";

import CollectionRateChart from "@/components/CollectionRateChart";
import LoanTable from "@/components/LoanTable";
import RevenueChart from "@/components/RevenueChart";
import DisbursementChart from "@/components/DisbursementChart";
import { LoanMonthData, MonthlyPerfApiRes, fmtKES, fmtShort } from "@/data/loanData";
import DatePick from "@/components/DatePick";

export default function MonthlyPerfomancePage() {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date(2025, 2, 1)); // March 1, 2025
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date(2026, 1, 28)); // Feb 28, 2026

  const [rows, setRows] = useState<LoanMonthData[]>([]);
  const [totals, setTotals] = useState<MonthlyPerfApiRes["totals"] | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiFrom = useMemo(() => (dateFrom ? format(dateFrom, "yyyy-MM-dd") : ""), [dateFrom]);
  const apiTo = useMemo(() => (dateTo ? format(dateTo, "yyyy-MM-dd") : ""), [dateTo]);

  const load = async () => {
    if (!apiFrom || !apiTo) return;

    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<MonthlyPerfApiRes>(`/management/monthly-performance?from=${apiFrom}&to=${apiTo}`);
      setRows(json.data || []);
      setTotals(json.totals || null);
    } catch (e: any) {
      setError(e?.message || "Failed to load report");
      setRows([]);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rangeLabel = useMemo(() => {
    const a = dateFrom ? format(dateFrom, "MMM yyyy") : "-";
    const b = dateTo ? format(dateTo, "MMM yyyy") : "-";
    return `${a} — ${b}`;
  }, [dateFrom, dateTo]);

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2 min-w-0"
        >
          <Banknote className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0" />
          <span className="truncate">Monthly Performance</span>
        </motion.h1>

        {/* Right side (range chip) */}
        <div className="flex items-center gap-2 sm:justify-end">
          <span className="inline-flex max-w-full truncate rounded-full border border-border bg-background px-3 py-1 text-xs sm:text-sm text-muted-foreground font-medium">
            {rangeLabel}
          </span>
        </div>
      </div>

      <ManagementReportMenu />

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            {/* Filters grid: stacks nicely on mobile, single row on md+ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 sm:gap-3 items-end">
              <div className="lg:col-span-4">
                <DatePick label="From" value={dateFrom} onChange={setDateFrom} />
              </div>

              <div className="lg:col-span-4">
                <DatePick label="To" value={dateTo} onChange={setDateTo} />
              </div>

              <div className="lg:col-span-4">
                <Button className="w-full" size="sm" onClick={load} disabled={loading || !apiFrom || !apiTo}>
                  <RefreshCcw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
                  {loading ? "Loading..." : "Reload"}
                </Button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <Card className="border-destructive/40">
                <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
              </Card>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main container (responsive padding) */}
      <main className="mx-auto w-full max-w-6xl px-3 sm:px-6 pb-4 sm:pb-6 space-y-5 sm:space-y-6 min-w-0">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            title="Total Revenue"
            value={totals ? fmtShort(totals.revenue) : "-"}
            subtitle={totals ? `KES ${fmtKES(totals.revenue)}` : "—"}
            icon={DollarSign}
            trend="up"
          />
          <StatCard
            title="Total Disbursed"
            value={totals ? fmtShort(totals.totalDisbursed) : "-"}
            subtitle={totals ? `Loans: ${fmtKES(totals.loanIssued)}` : "—"}
            icon={TrendingUp}
            trend="neutral"
          />
          <StatCard
            title="Total Collected"
            value={totals ? fmtShort(totals.collected) : "-"}
            subtitle={totals ? `Outstanding: KES ${fmtKES(totals.outstanding)}` : "—"}
            icon={BarChart3}
            trend="up"
          />
          <StatCard
            title="Total Customers"
            value={totals ? fmtKES(totals.customers) : "-"}
            subtitle={totals ? `Leads: ${fmtKES(totals.leads)}` : "—"}
            icon={Users}
            trend="neutral"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 min-w-0">
          <div className="min-w-0">
            <DisbursementChart rows={rows} />
          </div>
          <div className="min-w-0">
            <RevenueChart rows={rows} />
          </div>
        </div>

        <div className="min-w-0">
          <CollectionRateChart rows={rows} />
        </div>

        {/* Table (fix overflow) */}
        <section className="min-w-0">
          {/* Hard stop: prevents global horizontal scroll */}
          <div className="w-full max-w-full overflow-x-hidden">
            {/* Horizontal scroll only for the table region */}
            <div className="overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]">
              <div className="min-w-[900px] w-max">
               
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}